import {
  CorrespondenceMessage,
  RecordLink,
  ScoutingPhoto,
  ScoutingPhotoCategory,
  ScoutingPhotoUploadRequest,
} from '../types';
import { db } from './db';
import { attemptSessionRenewal, clearAuthSession, getAuthToken } from './auth';

const getEnv = (key: string): string => {
  if (typeof process !== 'undefined' && process.env && typeof process.env[key] === 'string') {
    return process.env[key] as string;
  }
  if (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env[key]) {
    return (import.meta as any).env[key];
  }
  return '';
};

const API_BASE = getEnv('VITE_API_URL') || '';
const buildUrl = (path: string, query?: Record<string, string | string[] | undefined>) => {
  const search = new URLSearchParams();
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        if (value.length) search.set(key, value.join(','));
      } else if (String(value)) {
        search.set(key, String(value));
      }
    });
  }
  const qs = search.toString();
  return `${API_BASE}/api/scouting${path}${qs ? `?${qs}` : ''}`;
};

const buildPublicUrl = (path: string) => `${API_BASE}/api/scouting/public${path}`;

const responseOwners = new WeakMap<Response, { database: typeof db; token: string | null }>();
const accountFetch: typeof fetch = async (...args) => {
  const database = db, token = getAuthToken();
  const response = await fetch(...args);
  if (database !== db || token !== getAuthToken()) throw new Error('Workspace changed; response ignored.');
  responseOwners.set(response, { database, token });
  return response;
};
const handleResponse = async <T>(response: Response): Promise<T> => {
  const owner = responseOwners.get(response);
  if (owner && owner.database !== db) throw new Error('Workspace changed; response ignored.');
  if (response.status === 401) {
    const renewed = await attemptSessionRenewal();
    if (owner && owner.database !== db) throw new Error('Workspace changed; renewal ignored.');
    if (!renewed) {
      if (!owner || owner.token === getAuthToken()) clearAuthSession();
      throw new Error('Unauthorized');
    }
    throw new Error('Session renewed. Please retry request.');
  }
  if (response.status === 403) {
    throw new Error('Forbidden');
  }
  if (response.status === 404) {
    throw new Error('Not found');
  }
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const body = await response.json();
      if (typeof body?.message === 'string' && body.message.trim()) message = body.message;
      else if (typeof body?.error === 'string' && body.error.trim()) message = body.error;
      else if (typeof body?.error_code === 'string' && body.error_code.trim()) message = body.error_code;

      if (body?.debug && typeof body.debug === 'object') {
        const debugCode = typeof body.debug.code === 'string' ? body.debug.code : '';
        const debugConstraint = typeof body.debug.constraint === 'string' ? body.debug.constraint : '';
        const debugDetail = typeof body.debug.detail === 'string' ? body.debug.detail : '';
        const summary = [debugCode, debugConstraint, debugDetail].filter(Boolean).join(' | ');
        if (summary) {
          message = `${message} (${summary})`;
        }
      }
    } catch {
      const text = await response.text();
      if (text?.trim()) message = text;
    }
    throw new Error(message);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const result=await response.json();
  if(owner&&(owner.database!==db||owner.token!==getAuthToken()))throw new Error('Workspace changed; response ignored.');
  return result;
};

const authHeaders = () => {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const normalizePhoto = (photo: ScoutingPhoto): ScoutingPhoto => ({
  ...photo,
  image_url: photo.image_url || photo.storage_url,
  thumbnail_url: photo.thumbnail_url || photo.image_url || photo.storage_url,
  display_url_expires_at: photo.display_url_expires_at ?? null,
  display_url_ttl_seconds: photo.display_url_ttl_seconds ?? null,
  tag_category_ids: photo.tag_category_ids || [],
});

const stripDataUrlPrefix = (value: string) => {
  const commaIndex = value.indexOf(',');
  return commaIndex >= 0 ? value.slice(commaIndex + 1) : value;
};

const flattenUploadPayload = (payload: ScoutingPhotoUploadRequest) => {
  const taken_at = payload.taken_at || payload.exif?.taken_at;
  return {
    photo_id: payload.photo_id,
    farm_id: payload.farm_id,
    place_tag: payload.place_tag,
    source: payload.source,
    created_by: payload.created_by,
    uploaded_at: payload.uploaded_at,
    taken_at,
    exif_lat: payload.exif?.location?.lat,
    exif_lng: payload.exif?.location?.lng,
    exif_alt: payload.exif?.location?.alt,
    notes: payload.notes,
    tag_category_ids: payload.tag_category_ids,
    file_base64: stripDataUrlPrefix(payload.file_base64),
    file_name: payload.file_name,
  };
};


export type PublicBackgroundDebug = {
  tag?: string;
  limit?: number;
  selectedRows?: number;
  returnedPhotos?: number;
  totals?: {
    total_photos?: number | string;
    primary_matches?: number | string;
    linked_matches?: number | string;
  } | null;
  reason?: string;
};

export type ScoutingPhotoFilters = {
  categoryId?: string;
  source?: string;
  createdBy?: string;
  startDate?: string;
  endDate?: string;
  tagCategoryIds?: string[];
  farmId?: string;
  tractId?: string;
  fieldId?: string;
  assetId?: string;
  placeId?: string;
};

export const ScoutingClient = {
  async listPublicBackgroundUrls(options?: { debug?: boolean }): Promise<{ urls: string[]; debugHeader?: string | null; debug?: PublicBackgroundDebug }> {
    const response = await accountFetch(buildPublicUrl(`/backgrounds${options?.debug ? '?debug=1' : ''}`));
    const payload = await handleResponse<{
      photos: { image_url?: string; thumbnail_url?: string; storage_url?: string }[];
      debug?: PublicBackgroundDebug;
    }>(response);
    const urls = Array.isArray(payload?.photos)
      ? payload.photos
          .map((photo) => photo?.image_url || photo?.thumbnail_url || photo?.storage_url || null)
          .filter((url): url is string => typeof url === 'string' && url.length > 0)
      : [];
    return {
      urls,
      debugHeader: response.headers.get('x-sonata-bg-debug'),
      debug: payload?.debug,
    };
  },

  async listPhotos(filters: ScoutingPhotoFilters = {}): Promise<ScoutingPhoto[]> {
    const query: Record<string, string | string[] | undefined> = {
      categoryId: filters.categoryId,
      source: filters.source,
      createdBy: filters.createdBy,
      startDate: filters.startDate,
      endDate: filters.endDate,
      tagCategoryIds: filters.tagCategoryIds,
      farmId: filters.farmId,
      tractId: filters.tractId,
      fieldId: filters.fieldId,
      assetId: filters.assetId,
      placeId: filters.placeId,
    };
    const response = await accountFetch(buildUrl('/photos', query), { headers: authHeaders() });
    const photos = await handleResponse<ScoutingPhoto[]>(response);
    return photos.map(normalizePhoto);
  },

  async getPhoto(photoId: string,audience?:'work'|'nature'): Promise<ScoutingPhoto | undefined> {
    const response = await accountFetch(buildUrl(`/photos/${encodeURIComponent(photoId)}`,{audience}), { headers: authHeaders() });
    const photo = await handleResponse<ScoutingPhoto | undefined>(response);
    return photo ? normalizePhoto(photo) : undefined;
  },

  async listCategories(): Promise<ScoutingPhotoCategory[]> {
    const response = await accountFetch(buildUrl('/categories'), { headers: authHeaders() });
    return handleResponse<ScoutingPhotoCategory[]>(response);
  },

  async createCategory(category: ScoutingPhotoCategory): Promise<ScoutingPhotoCategory> {
    const response = await accountFetch(buildUrl('/categories'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(category),
    });
    return handleResponse<ScoutingPhotoCategory>(response);
  },

  async updateCategory(categoryId: string, category: Partial<ScoutingPhotoCategory>): Promise<ScoutingPhotoCategory> {
    const response = await accountFetch(buildUrl(`/categories/${categoryId}`), {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(category),
    });
    return handleResponse<ScoutingPhotoCategory>(response);
  },

  async deleteCategory(categoryId: string): Promise<void> {
    const response = await accountFetch(buildUrl(`/categories/${categoryId}`), {
      method: 'DELETE',
      headers: authHeaders(),
    });
    await handleResponse(response);
  },

  async listPlacePhotoLinks(placeId: string): Promise<RecordLink[]> {
    const response = await accountFetch(buildUrl('/places/'+encodeURIComponent(placeId)+'/links'), {headers:authHeaders()});
    return handleResponse<RecordLink[]>(response);
  },

  async listPhotoLinks(photoId: string): Promise<RecordLink[]> {
    const response = await accountFetch(buildUrl(`/photos/${photoId}/links`), { headers: authHeaders() });
    return handleResponse<RecordLink[]>(response);
  },

  async upsertPhotoLink(photoId: string, link: Pick<RecordLink, 'entity_type' | 'entity_id'> & { record_link_id?: string }): Promise<RecordLink> {
    const response = await accountFetch(buildUrl(`/photos/${photoId}/links`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(link),
    });
    return handleResponse<RecordLink>(response);
  },

  async deletePhotoLink(photoId: string, linkId: string): Promise<void> {
    const response = await accountFetch(buildUrl(`/photos/${photoId}/links/${linkId}`), {
      method: 'DELETE',
      headers: authHeaders(),
    });
    await handleResponse(response);
  },

  async addComment(photoId: string, payload: { body_text: string; channel?: string; direction?: string }): Promise<{ message_id: string }> {
    const response = await accountFetch(buildUrl(`/photos/${photoId}/comments`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  async listComments(photoId: string): Promise<CorrespondenceMessage[]> {
    const response = await accountFetch(buildUrl(`/photos/${photoId}/comments`), { headers: authHeaders() });
    return handleResponse<CorrespondenceMessage[]>(response);
  },

  async upsertPhoto(photo: ScoutingPhoto): Promise<ScoutingPhoto> {
    const editable={...photo} as Record<string,unknown>;
    for(const key of ['farm_id','first_recorded_at','revision','uploader_email','digest','finalized_at','excluded','provenance'])delete editable[key];
    const hasId = Boolean(photo.photo_id);
    const method = hasId ? 'PUT' : 'POST';
    const path = hasId ? `/photos/${photo.photo_id}` : '/photos';
    const response = await accountFetch(buildUrl(path), {
      method,
      headers: authHeaders(),
      body: JSON.stringify(editable),
    });
    const saved = await handleResponse<ScoutingPhoto>(response);
    return normalizePhoto(saved);
  },

  async setPhotoTags(photoId:string,tagCategoryIds:string[]):Promise<void>{
    const response=await accountFetch(buildUrl(`/photos/${encodeURIComponent(photoId)}`),{method:'PUT',headers:authHeaders(),body:JSON.stringify({tag_category_ids:tagCategoryIds})});
    await handleResponse(response);
  },

  async uploadPhoto(payload: ScoutingPhotoUploadRequest): Promise<ScoutingPhoto> {
    const response = await accountFetch(buildUrl('/photos/upload'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(flattenUploadPayload(payload)),
    });
    const saved = await handleResponse<ScoutingPhoto>(response);
    return normalizePhoto(saved);
  },

  async deletePhoto(photoId: string): Promise<void> {
    const response = await accountFetch(buildUrl(`/photos/${photoId}`), {
      method: 'DELETE',
      headers: authHeaders(),
    });
    await handleResponse(response);
  },
};
